import axios from 'axios';
import jwtDecode from 'jwt-decode';
import * as types from '../constants/types';
import * as globals from '../constants/globals';
import { doneLoading, startLoading } from './loading';
import { fetchAlerts } from './alertsNotification';
import { JSONContentType, makeAuthorizedRequest } from '../utilities/rest';
import { setAuthStorage } from '../utilities/internalSecurityUtil';

const loginPath = globals.LOGIN_PATH;
const dashBoardPath = globals.DASHBOARD_PATH;
const excludeLoginPaths = globals.EXCLUDE_LOGIN_PATHS;
const idleMonitorStorageKey = 'idlemonitor';
export const loginMessagesStorageKey = 'loginMessages';
const inactivityTimeout = 30 * 60 * 1000;

const loggedOut = () => {
  return {
    type: types.LOGGED_OUT,
  };
};

const mustSetPassword = (token, history) => {
  history.push(`/internal/account/reset-password?token=${token}`);
  return {
    type: types.LOGGED_OUT_FOR_MUST_SET_PASSWORD,
  };
};

//Should probably only be called by the logout action unless not logged in. Fight me!
//Also note that this gets overwriten by InternalProtectedRoute in most cases so if stuff is weird look there.
export const navigateLogin = (prevPathname) => {
  let redirectTo = loginPath;

  if (prevPathname && prevPathname !== loginPath) {
    redirectTo = `${loginPath}?redirect=${prevPathname}`;
  }

  window.location.assign(redirectTo);
};

export const doLogout = async (dispatch) => {
  try {
    await makeAuthorizedRequest(`${globals.API_URL}/logout`, 'POST', dispatch, {});
  } catch (error) {
    // Todo: Log to Sentry
  }
};

export const logOut = (prevPathname) => {
  doLogout(); // Ignore promise b/c the user doesn't care
  localStorage.removeItem(globals.TOKEN_LOCAL_STORAGE_KEY);
  localStorage.removeItem('persist:root');
  if (window.sessionStorage) {
    window.sessionStorage.clear();
  }
  navigateLogin(prevPathname);
  return loggedOut();
};

export const loginFailed = (error) => {
  return {
    type: types.LOGIN_FAILED,
    error,
  };
};

export const setEmail = (email) => {
  return {
    type: types.SET_EMAIL,
    email,
  };
};

export const displayAutoLogoutWarning = () => {
  return {
    type: types.DISPLAY_AUTO_LOGOUT_WARNING,
  };
};

export const closeAutoLogoutWarning = () => {
  return {
    type: types.CLOSE_AUTO_LOGOUT_WARNING,
  };
};

export const loginSuccessful = (account) => {
  return (dispatch, getState) => {
    let selectedRole;
    const state = getState();

    if (state.selectedLocationRole && state.selectedLocationRole.role) {
      selectedRole = state.selectedLocationRole.role;
    } else {
      let persistedRole;

      const persistedRoleString = localStorage.getItem(`persist:${globals.PERSIST_KEY}:selectedLocationRole`);
      if (persistedRoleString) {
        try {
          persistedRole = JSON.parse(persistedRoleString);
        } catch (err) {
          persistedRole = false;
        }
      }

      if (persistedRole && persistedRole.role) {
        try {
          selectedRole = JSON.parse(persistedRole.role);
        } catch (err) {
          selectedRole = false;
        }
      }
    }

    if (!selectedRole) {
      let initiallySelectedRole = account.user.roles.find((r) => r.is_default_role);

      if (!initiallySelectedRole) {
        initiallySelectedRole = account.user.roles.length > 0 ? account.user.roles[0] : null;
      }

      selectedRole = initiallySelectedRole;
    }

    if (selectedRole) {
      dispatch(fetchAlerts(selectedRole.location));
    }

    dispatch({
      type: types.LOGIN_SUCCESSFUL,
      initiallySelectedRole: selectedRole,
      account,
    });
  };
};

export const foundNewUser = () => {
  return {
    type: types.FOUND_NEW_USER,
  };
};

export const logOutForInactivity = () => {
  return (dispatch) => {
    dispatch(logOut(window.location.pathname));
    dispatch({
      type: types.LOGGED_OUT_FOR_INACTIVITY,
    });
  };
};

export const checkLoggedIn = (fromLocation, history) => {
  return (dispatch, getState) => {
    const pathName = window.location.pathname;
    if (excludeLoginPaths.indexOf(pathName) === -1 || fromLocation === '/internal/account/confirm-email') {
      const lastActiveEntryString = localStorage.getItem(idleMonitorStorageKey);

      if (lastActiveEntryString) {
        const lastActiveEntry = JSON.parse(lastActiveEntryString);
        const lastActivePastLimit = lastActiveEntry.lastActive + inactivityTimeout < Date.now();

        if (pathName !== loginPath && (!lastActiveEntry.isActive || lastActivePastLimit)) {
          const loginMessages = JSON.stringify({ loggedOutForInactivity: true });
          localStorage.setItem(loginMessagesStorageKey, loginMessages);
          dispatch(logOutForInactivity());
        }
      }

      const state = getState();

      const tokenString = localStorage.getItem(globals.TOKEN_LOCAL_STORAGE_KEY);
      const tokenObject = tokenString ? JSON.parse(tokenString) : null;

      const invalid =
        !tokenObject || !tokenObject.access_token || !jwtDecode(tokenObject.access_token).exp >= Date.now() / 1000;
      const needsToSetPasswordForFistTime =
        tokenObject &&
        tokenObject.user &&
        (tokenObject.user.password_set_at === globals.EMPTY_TIME_STRING || tokenObject.user.password_set_at == null);
      const needsToChangePassword = tokenObject && tokenObject.user && tokenObject.user.must_set_password;

      if (invalid) {
        if (!pathName.startsWith(globals.REPORT_DOWNLOAD_PATH) && pathName !== loginPath) {
          dispatch(logOut(fromLocation ? fromLocation.pathname : '/'));
        }
      } else if (!invalid && needsToSetPasswordForFistTime) {
        dispatch(mustSetPassword(tokenObject.access_token, history));
      } else if (!invalid && needsToChangePassword) {
        dispatch(loginSuccessful(tokenObject));
        dispatch(foundNewUser());

        history.push('/internal/account/change-password');
      } else if (
        !invalid &&
        !needsToSetPasswordForFistTime &&
        !needsToChangePassword &&
        pathName === loginPath &&
        history
      ) {
        // user is logged in, doesn't need to set or change a password, and we are on the login page.  Likely we are here
        // because the user had a book mark or manually navigated to the login page. Send them to the dashboard
        history.push(dashBoardPath);
      } else if (!state.login.user) {
        dispatch(loginSuccessful(tokenObject));
      } else if (state.login.user && state.login.user.user_id !== tokenObject.user.user_id) {
        dispatch(loginSuccessful(tokenObject));
        dispatch(foundNewUser());
      }
    }
  };
};

export const ensureLoggedInUserInState = (history) => {
  return (dispatch, getState) => {
    const state = getState();

    if (!state.login.user) {
      const tokenString = localStorage.getItem(globals.TOKEN_LOCAL_STORAGE_KEY);
      const tokenObject = tokenString ? JSON.parse(tokenString) : null;

      if (tokenObject) {
        dispatch(loginSuccessful(tokenObject));
        dispatch(foundNewUser());
      } else {
        history.replace('/internal/account/login');
        // dispatch(navigateLogin(window.location.pathname));
      }
    }
  };
};

export const forgotPassword = (email, history) => {
  history.push('/internal/account/recover-password');
  return (dispatch) => {
    dispatch(setEmail(email));
  };
};

export const logIn = (email, password, nextPathname, history) => {
  return async (dispatch) => {
    const postData = {
      email,
      password,
    };

    const tokenString = localStorage.getItem(globals.TOKEN_LOCAL_STORAGE_KEY);
    const tokenObject = tokenString ? JSON.parse(tokenString) : null;
    if (tokenObject) {
      // This must be cleared in the case that a user has two login screens open.
      // The user could potentially login with one, do some work, walk away, come back and log in
      // with the other. This will clear the previously selected role and set the new login state
      // to either the default role or the 0th role. -jkeene
      localStorage.removeItem(`persist:${globals.PERSIST_KEY}:selectedLocationRole`);
    }
    try {
      dispatch(startLoading('Signing In...'));

      const setActiveItem = JSON.stringify({ isActive: true, lastActive: Date.now() });
      localStorage.setItem(idleMonitorStorageKey, setActiveItem);
      localStorage.removeItem(loginMessagesStorageKey);

      const headers = new Headers();
      headers.append('Content-Type', JSONContentType);

      const response = await axios.post(`${globals.API_URL}/login`, postData, {
        withCredentials: true,
        credentials: 'include',
        headers,
      });

      const loginResponseData = response?.data;
      if (response.status >= 200 && response.status < 300) {
        setAuthStorage(loginResponseData);
        dispatch(loginSuccessful(loginResponseData));

        if (
          loginResponseData.user &&
          loginResponseData.user.must_set_password &&
          loginResponseData.user.password_set_at !== globals.EMPTY_TIME_STRING &&
          loginResponseData.user.password_set_at != null
        ) {
          history.push('/internal/account/change-password');
        } else if (nextPathname && typeof nextPathname === 'string') {
          //needs to be window.location.assign because history.push doesn't work across microservices.
          window.location.assign(`${nextPathname}?redirect=${nextPathname}`);
        } else {
          history.push('/internal/account/hub');
        }
      } else {
        throw new Error(loginResponseData.error);
      }
      dispatch(checkLoggedIn());
    } catch (error) {
      dispatch(loginFailed(error));
    } finally {
      dispatch(doneLoading());
    }
  };
};

export const autoLogoutWarningCanceled = () => {
  return (dispatch) => {
    dispatch(closeAutoLogoutWarning());
    dispatch(logOut(window.location.pathname));
  };
};

export const forceLogOut = (error) => {
  return (dispatch) => {
    dispatch(logOut(window.location.pathname));
    dispatch(loginFailed(error));
  };
};
